import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ChatMessage,
  ClientMessage,
  ErrorCode,
  Gender,
  PeerState,
  RoomSettings,
  ServerMessage,
  Slot,
} from '@tolk/shared';

/**
 * De verbinding met de kamer.
 *
 * Het lastigste deel is NIET het verbinden maar het OPNIEUW verbinden. Een
 * telefoon in het buitenland wisselt van zendmast, gaat door een tunnel, of
 * valt in slaap in je zak. Zonder herstel zit je dan buiten je eigen gesprek.
 *
 * Daarom: het sessietoken gaat in sessionStorage en gaat mee bij elke poging.
 * De server geeft je daarmee je eigen plek terug in plaats van "kamer vol".
 */

export type Verbindingsstand =
  | 'verbinden'
  | 'verbonden'
  | 'opnieuw_verbinden'
  | 'geweigerd'
  | 'gesloten';

export interface KamerVerbinding {
  stand: Verbindingsstand;
  slot: Slot | null;
  settings: RoomSettings | null;
  peer: PeerState;
  /** Welke kant volgens de server nu spreekt. Puur informatief. */
  turn: Slot | null;
  berichten: ChatMessage[];
  foutcode: ErrorCode | null;
  /** Wat de server verstond, wachtend op goedkeuring. Alleen voor de spreker. */
  concept: { clientMsgId: string; bron: string; vertaling: string } | null;
  wisConcept: () => void;
  /** Een melding wegklikken die het gesprek niet stopt, zoals spraak die faalde. */
  wisFout: () => void;
  /** Hoeveelste herstelpoging. 0 zolang alles goed gaat. */
  poging: number;
  stuur: (bericht: ClientMessage) => void;
  opnieuw: () => void;
  /**
   * Opnieuw binnenkomen en de plek van mijn taal OVERNEMEN, ook als hij bezet
   * is. Alleen na plek_bezet en nadat de gebruiker WISSEL typte.
   */
  neemOver: () => void;
}

export interface BinnengekomenAudio {
  messageId: string;
  base64: string;
  sampleRate: number;
  /** Telt vanaf 0. Boven de 0 is dit een vervolgstuk van dezelfde zin. */
  deel: number;
  laatste: boolean;
}

export interface BinnengekomenBronAudio {
  messageId: string;
  mimeType: string;
  base64: string;
}

export interface VerbindOpties {
  /** Wordt aangeroepen zodra er spraak binnenkomt. */
  opAudio?: (audio: BinnengekomenAudio) => void;
  /** De opname van de spreker zelf. */
  opBronAudio?: (audio: BinnengekomenBronAudio) => void;
  kamerId: string;
  pin: string | null;
  lang: string;
  gender: Gender;
  /** false zolang de gebruiker de pincode nog niet heeft ingevuld. */
  actief: boolean;
  /**
   * Spraak voor mij meteen laten maken (true), of pas als ik op ▶ druk. Het
   * vinkje "Vertaling meteen afspelen": staat dat uit, dan is spraak maken die
   * niemand hoort geld voor niets. Zie publiceer() op de server.
   */
  autoSpraak: boolean;
}

/** Fouten waarbij opnieuw proberen zinloos is: die zijn definitief. */
const DEFINITIEF = new Set<ErrorCode>([
  // ZONDER DEZE REGEL KRIJG JE PINGPONG. De server sluit deze verbinding na een
  // overname; zou de client daarna gewoon opnieuw verbinden, dan meldt hij zich
  // met hetzelfde token en pakt hij de plek meteen terug - waarna het andere
  // apparaat eruit vliegt, dat ook opnieuw verbindt, en zo door. Een overname
  // is definitief: dit apparaat doet niets meer.
  'overgenomen',
  'room_not_found',
  'room_expired',
  'room_full',
  // Opnieuw proberen pakt de plek niet: hij is bezet tot de ander weggaat. De
  // gebruiker kiest zelf, met WISSEL, of hij hem overneemt.
  'plek_bezet',
  'pin_required',
  'pin_wrong',
  'protocol_mismatch',
]);

/** Oplopend wachten, met een bovengrens. Niet elke 100 ms blijven rammen. */
function wachttijd(poging: number): number {
  return Math.min(500 * 2 ** Math.min(poging, 5), 15_000);
}

function tokenSleutel(kamerId: string): string {
  return `tolk.token.${kamerId}`;
}

function leesToken(kamerId: string): string | null {
  try {
    return sessionStorage.getItem(tokenSleutel(kamerId));
  } catch {
    // Privémodus of geblokkeerde opslag. Dan werkt het gesprek gewoon, alleen
    // herstel na netwerkverlies niet - dat is geen reden om te stoppen.
    return null;
  }
}

function bewaarToken(kamerId: string, token: string): void {
  try {
    sessionStorage.setItem(tokenSleutel(kamerId), token);
  } catch {
    // Zie leesToken.
  }
}

export function useRoomSocket(opties: VerbindOpties): KamerVerbinding {
  const { kamerId, pin, lang, gender, actief, autoSpraak } = opties;
  // In een ref: de lus mag niet opnieuw opstarten als de aanroeper een nieuwe
  // functie doorgeeft bij elke render.
  const opAudioRef = useRef(opties.opAudio);
  opAudioRef.current = opties.opAudio;
  const opBronRef = useRef(opties.opBronAudio);
  opBronRef.current = opties.opBronAudio;

  const [stand, setStand] = useState<Verbindingsstand>('verbinden');
  const [slot, setSlot] = useState<Slot | null>(null);
  const [settings, setSettings] = useState<RoomSettings | null>(null);
  const [peer, setPeer] = useState<PeerState>({ present: false, typing: '' });
  const [turn, setTurn] = useState<Slot | null>(null);
  const [berichten, setBerichten] = useState<ChatMessage[]>([]);
  const [foutcode, setFoutcode] = useState<ErrorCode | null>(null);
  const [poging, setPoging] = useState(0);
  const [herstart, setHerstart] = useState(0);
  const [concept, setConcept] = useState<KamerVerbinding['concept']>(null);

  const socketRef = useRef<WebSocket | null>(null);
  // In een ref en niet in state: de opnieuw-verbindenlus moet deze waarden
  // lezen zonder dat een wijziging de lus opnieuw opstart.
  const gegevensRef = useRef({ pin, lang, gender, autoSpraak });
  gegevensRef.current = { pin, lang, gender, autoSpraak };

  const stuur = useCallback((bericht: ClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(bericht));
  }, []);

  // Het vinkje omgezet midden in het gesprek: meteen doorgeven, zodat de
  // VOLGENDE zin er al naar luistert. Bij binnenkomst gaat hij al mee in join;
  // dit is dan een overbodige tweede keer, en dat mag.
  useEffect(() => {
    stuur({ type: 'settings', autoSpraak });
  }, [autoSpraak, stuur]);

  const opnieuw = useCallback(() => {
    setFoutcode(null);
    setPoging(0);
    setHerstart((n) => n + 1);
  }, []);

  /**
   * Gaat alleen mee met de EERSTVOLGENDE join. Daarna heb je een token, en
   * dat geeft je je plek bij elke herverbinding terug. Zou de vlag blijven
   * staan, dan pakt een herverbinding zonder token de plek stil weer af van
   * wie hem intussen rechtmatig heeft.
   */
  const overnemenRef = useRef(false);
  const neemOver = useCallback(() => {
    overnemenRef.current = true;
    opnieuw();
  }, [opnieuw]);

  useEffect(() => {
    if (!actief) return;

    let gestopt = false;
    let mislukt = 0;
    let hertimer: number | undefined;

    const verbind = (): void => {
      if (gestopt) return;

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const socket = new WebSocket(`${protocol}//${window.location.host}/ws`);
      socketRef.current = socket;

      socket.onopen = () => {
        if (gestopt) return;
        const bewaard = leesToken(kamerId);
        const { pin: p, lang: l, gender: g, autoSpraak: a } = gegevensRef.current;
        socket.send(
          JSON.stringify({
            type: 'join',
            roomId: kamerId,
            pin: p,
            lang: l,
            gender: g,
            autoSpraak: a,
            ...(bewaard !== null ? { token: bewaard } : {}),
            ...(overnemenRef.current ? { overnemen: true } : {}),
          } satisfies ClientMessage),
        );
      };

      socket.onmessage = (gebeurtenis) => {
        let bericht: ServerMessage;
        try {
          bericht = JSON.parse(String(gebeurtenis.data)) as ServerMessage;
        } catch {
          return;
        }

        switch (bericht.type) {
          case 'joined':
            overnemenRef.current = false;
            mislukt = 0;
            setPoging(0);
            setStand('verbonden');
            setSlot(bericht.slot);
            setSettings(bericht.settings);
            setPeer(bericht.peer);
            setBerichten(bericht.history);
            setFoutcode(null);
            // Een stale beurt van vóór het verbreken mag niet blijven staan.
            // Was de ander nog aan het woord, dan corrigeert de server dat
            // direct hierna met een eigen 'turn'-bericht.
            setTurn(null);
            break;
          case 'sessieToken':
            bewaarToken(kamerId, bericht.token);
            break;
          case 'peerState':
            setPeer(bericht.peer);
            break;
          case 'turn':
            setTurn(bericht.slot);
            break;
          case 'bronAudio':
            opBronRef.current?.({
              messageId: bericht.messageId,
              mimeType: bericht.audio.mimeType,
              base64: bericht.audio.base64,
            });
            break;
          case 'concept':
            setConcept({
              clientMsgId: bericht.clientMsgId,
              bron: bericht.bron,
              vertaling: bericht.vertaling,
            });
            break;
          case 'audio':
            opAudioRef.current?.({
              messageId: bericht.messageId,
              base64: bericht.base64,
              sampleRate: bericht.sampleRate,
              deel: bericht.deel,
              laatste: bericht.laatste,
            });
            break;
          case 'settingsChanged':
            setSettings(bericht.settings);
            break;
          case 'budgetVrij':
            // Alleen de BUDGETmelding weg. Stond er intussen een andere fout,
            // dan heeft die niets met het verhoogde budget te maken en blijft
            // hij staan.
            setFoutcode((oud) => (oud === 'budget_room' || oud === 'budget_month' ? null : oud));
            break;
          case 'message':
            setBerichten((oud) => [...oud, bericht.message]);
            break;
          case 'messageUpdate':
            setBerichten((oud) =>
              oud.map((b) => (b.id === bericht.message.id ? bericht.message : b)),
            );
            break;
          case 'error':
            if (bericht.code === 'plek_bezet') overnemenRef.current = false;
            setFoutcode(bericht.code);
            if (DEFINITIEF.has(bericht.code)) {
              gestopt = true;
              setStand('geweigerd');
            }
            break;
          case 'roomClosed':
            gestopt = true;
            setStand('gesloten');
            break;
          default:
            break;
        }
      };

      socket.onclose = () => {
        if (gestopt) return;
        // Wegvallen is normaal, niet uitzonderlijk. Gewoon opnieuw proberen.
        setStand('opnieuw_verbinden');
        mislukt += 1;
        setPoging(mislukt);
        hertimer = window.setTimeout(verbind, wachttijd(mislukt));
      };

      socket.onerror = () => socket.close();
    };

    verbind();

    return () => {
      gestopt = true;
      if (hertimer !== undefined) window.clearTimeout(hertimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [kamerId, actief, herstart]);

  const wisConcept = useCallback(() => setConcept(null), []);
  /**
   * Een melding wegklikken.
   *
   * Nodig voor fouten die het gesprek NIET stoppen, zoals spraak die niet
   * lukte. Die blijven anders staan tot de volgende fout, en dan lijkt een oud
   * probleem een nieuw probleem.
   */
  const wisFout = useCallback(() => setFoutcode(null), []);

  return {
    stand,
    slot,
    settings,
    peer,
    turn,
    berichten,
    foutcode,
    poging,
    concept,
    wisConcept,
    wisFout,
    stuur,
    opnieuw,
    neemOver,
  };
}
